import { DatePicker } from "@/common/components/DatePicker";
import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { useRecordWeight } from "../api/animalsApi";
import type { AnimalDetail } from "../types";

type Props = {
  accountId: string;
  animal: AnimalDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

type Source = "balance" | "tape";

export function RecordWeightDialog({ accountId, animal, open, onOpenChange, onSuccess }: Props) {
  const mutation = useRecordWeight(accountId, animal.id);

  const [measuredAt, setMeasuredAt] = useState<Date | undefined>(undefined);
  const [weightKg, setWeightKg] = useState<string>("");
  const [source, setSource] = useState<Source>("tape");
  const [notes, setNotes] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMeasuredAt(new Date());
    setWeightKg("");
    setSource("tape");
    setNotes("");
    setError(null);
  }, [open]);

  const birthDate = new Date(animal.birthDate);
  const today = new Date();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!measuredAt) {
      setError("Selecciona la fecha del pesaje.");
      return;
    }
    const weightNum = Number(weightKg.replace(",", "."));
    if (!Number.isFinite(weightNum) || weightNum <= 0) {
      setError("Ingresa un peso mayor a 0.");
      return;
    }
    if (weightNum >= 1500) {
      setError("El peso debe ser menor a 1500 kg.");
      return;
    }

    try {
      await mutation.mutateAsync({
        measuredAt: measuredAt.toISOString(),
        weightKg: weightNum.toFixed(2),
        source,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError("Ocurrió un error inesperado.");
    }
  }

  const pending = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar peso</DialogTitle>
          <DialogDescription>
            Indica si lo medís con balanza o con cinta torácica — afecta la precisión del cálculo de
            apreciación.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="weight-date" className="font-medium text-[13px] text-app-ink-2">
              Fecha del pesaje
            </label>
            <DatePicker
              id="weight-date"
              aria-label="Fecha del pesaje"
              value={measuredAt}
              onChange={setMeasuredAt}
              minDate={birthDate}
              maxDate={today}
              disabled={pending}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="weight-kg" className="font-medium text-[13px] text-app-ink-2">
              Peso (kg)
            </label>
            <input
              id="weight-kg"
              type="text"
              inputMode="decimal"
              value={weightKg}
              onChange={(e) => setWeightKg(e.target.value)}
              disabled={pending}
              placeholder="Ej. 425.5"
              className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="weight-source" className="font-medium text-[13px] text-app-ink-2">
              Origen de la medición
            </label>
            <Select value={source} onValueChange={(v) => setSource(v as Source)} disabled={pending}>
              <SelectTrigger id="weight-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="balance">Balanza (preciso)</SelectItem>
                <SelectItem value="tape">Cinta torácica (estimado)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="weight-notes" className="font-medium text-[13px] text-app-ink-2">
              Notas (opcional)
            </label>
            <textarea
              id="weight-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={pending}
              rows={2}
              maxLength={500}
              placeholder="Ej. Condición corporal 3.5"
              className="w-full resize-none rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Registrando…" : "Registrar peso"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
